import { ShieldCheck, ShieldOff, Zap } from 'lucide-react';
import { ChangeEvent, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Badge } from '../../../../../../../components/atoms/badge/badge';
import { Button } from '../../../../../../../components/atoms/button/button';
import { Input } from '../../../../../../../components/atoms/input/input';
import { useAuth } from '../../../../../../../hooks/use-auth';
import { useClienteD3 } from '../../../../../../../hooks/use-cliente-d3';
import { D3TaxaPayload } from '../../../../../../../types/cliente-d3';

interface Props {
  clienteId: string;
  isD3Enabled: boolean;
  onToggle: (isD3Enabled: boolean) => void;
}

const Card = styled.section`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  grid-column: 1 / -1;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled.div`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 28px;
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EMPTY_TAXA: D3TaxaPayload = {
  taxaInternacional: 0,
  reservaGlobal: 0,
  taxaExtraInternacional: 0,
  tempoReservaGlobal: 0,
  taxaNacional: 0,
  taxaPix: 0,
  reservaNacional: 0,
  taxaExtraNacional: 0,
  tempoReservaNacional: 0,
};

export const D3Card: React.FC<Props> = ({ clienteId, isD3Enabled, onToggle }) => {
  const { hasPermission } = useAuth();
  const { toggle, loading } = useClienteD3(clienteId);

  const canUpdate = hasPermission('CLIENTE_D3_UPDATE');

  const [showEnableModal, setShowEnableModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [taxa, setTaxa] = useState<D3TaxaPayload>({ ...EMPTY_TAXA });

  const handleEnable = async () => {
    if (!motivo.trim()) {
      toast.error('Informe o motivo para habilitar D+3.');
      return;
    }
    const result = await toggle({ enabled: true, motivo, taxa });
    if (result) {
      toast.success('D+3 habilitado com sucesso.');
      onToggle(true);
      setShowEnableModal(false);
      setMotivo('');
      setTaxa({ ...EMPTY_TAXA });
    } else {
      toast.error('Erro ao habilitar D+3. Tente novamente.');
    }
  };

  const handleDisable = async () => {
    const result = await toggle({ enabled: false });
    if (result) {
      toast.success('D+3 desabilitado.');
      onToggle(false);
      setShowDisableModal(false);
    } else {
      toast.error('Erro ao desabilitar D+3. Tente novamente.');
    }
  };

  const setTaxaField = (field: keyof D3TaxaPayload, value: string) => {
    setTaxa((prev) => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  return (
    <>
      <Card>
        <Header>
          <Title>
            <Zap size={14} />
            Recebimento D+3
          </Title>
          <Badge variant={isD3Enabled ? 'success' : 'neutral'}>
            {isD3Enabled ? 'Habilitado' : 'Desabilitado'}
          </Badge>
        </Header>

        {canUpdate && (
          <div style={{ display: 'flex', gap: 10 }}>
            {!isD3Enabled && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowEnableModal(true)}
              >
                <ShieldCheck size={14} />
                Habilitar D+3
              </Button>
            )}
            {isD3Enabled && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => setShowDisableModal(true)}
              >
                <ShieldOff size={14} />
                Desabilitar D+3
              </Button>
            )}
          </div>
        )}
      </Card>

      {showEnableModal && (
        <Overlay onClick={() => setShowEnableModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Habilitar D+3</ModalTitle>

            <FieldLabel>
              Motivo (obrigatório)
              <Input
                placeholder="Descreva o motivo da habilitação..."
                value={motivo}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setMotivo(e.target.value)
                }
              />
            </FieldLabel>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {([
                {
                  title: 'Taxas Nacionais',
                  fields: [
                    ['taxaNacional', 'Taxa Nacional (%)'],
                    ['taxaPix', 'Taxa PIX (%)'],
                    ['reservaNacional', 'Reserva Nacional (%)'],
                    ['taxaExtraNacional', 'Taxa Extra (R$)'],
                    ['tempoReservaNacional', 'Tempo Reserva (dias)'],
                  ],
                },
                {
                  title: 'Taxas Internacionais',
                  fields: [
                    ['taxaInternacional', 'Taxa Internacional (%)'],
                    ['reservaGlobal', 'Reserva Global (%)'],
                    ['taxaExtraInternacional', 'Taxa Extra (USD)'],
                    ['tempoReservaGlobal', 'Tempo Reserva (dias)'],
                  ],
                },
              ] as { title: string; fields: [keyof D3TaxaPayload, string][] }[]).map((section) => (
                <div key={section.title}>
                  <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'inherit' }}>
                    {section.title}
                  </p>
                  <FieldGroup>
                    {section.fields.map(([field, label]) => (
                      <FieldLabel key={field}>
                        {label}
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          value={taxa[field]}
                          onFocus={(e) => e.target.select()}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setTaxaField(field, e.target.value)
                          }
                        />
                      </FieldLabel>
                    ))}
                  </FieldGroup>
                </div>
              ))}
            </div>

            <ModalFooter>
              <Button
                variant="secondary"
                onClick={() => setShowEnableModal(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleEnable}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Confirmar habilitação'}
              </Button>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {showDisableModal && (
        <Overlay onClick={() => setShowDisableModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Desabilitar D+3</ModalTitle>
            <p style={{ margin: 0, fontSize: 14 }}>
              Tem certeza que deseja desabilitar o recebimento em D+3 para este
              cliente? Se o cliente estiver configurado com D+3 como tempo de
              recebimento, ele será revertido para D+30 automaticamente.
            </p>
            <ModalFooter>
              <Button
                variant="secondary"
                onClick={() => setShowDisableModal(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDisable}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Confirmar desabilitação'}
              </Button>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
    </>
  );
};
